/**
 * Shared cross-surface display formatters ('en-US' pinned — SSR hydration
 * safety per 0-lessons.md). Generic helpers consumed by the open-house
 * surfaces, the property page, and the agent-profile cards live here; the
 * open-house-specific day-palette/time-bucket helpers stay in
 * `open-house-format.ts`.
 */

/**
 * Compact listing price: ≥$1M → "$x.xM", ≥$1K → "$xK", else the full amount
 * ('en-US' pinned). One core for the week explorer's text and the map's pin
 * face (the map wraps it with its own null→"Open" case). ROUNDING NOTE: the
 * pin label historically used `Math.round(price / 1_000)` where this uses
 * `toFixed(0)` — for positive prices both round half-up, so the two are
 * arithmetically identical across the K branch.
 */
export function formatPriceCompact(price: number): string {
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(1)}M`;
  if (price >= 1_000) return `$${(price / 1_000).toFixed(0)}K`;
  return `$${price.toLocaleString("en-US")}`;
}

// Module-level formatter (one Intl construction, not per call/render).
const DATE_ONLY_UTC_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * Format a DATE-ONLY string ("2026-03-24") as "Mar 24, 2026" with a UTC pin —
 * parsing date-only strings without the pin shifts the day for any viewer
 * west of UTC. Unparseable/absent input → null (caller omits the line).
 */
export function formatDateOnlyUTC(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return DATE_ONLY_UTC_FMT.format(date);
}

// Module-level formatter (one Intl construction, not per call/render).
const INT_FMT = new Intl.NumberFormat("en-US");

/** Thousands-grouped integer ("2748" → "2,748"), 'en-US' pinned. */
export function formatInt(value: number): string {
  return INT_FMT.format(value);
}

/** First word of a display name ("Alice Johnson" → "Alice"); whole name when single-word. */
export function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** "CITY, ST ZIP" line parts joined gracefully (missing parts drop). Empty → null. */
export function cityStateZipLine(
  city: string | null,
  state: string | null,
  zip: string | null,
): string | null {
  const cityState = [city, state].filter(Boolean).join(", ");
  const line = [cityState, zip].filter(Boolean).join(" ");
  return line || null;
}
